import { createFileRoute } from '@tanstack/react-router';
import { Button, message, Tag } from 'antd';
import * as React from 'react';
import Wrapper from '../../../../components/Wrapper';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { customersGroupQueryOptions, salesPointsQueryOptions } from '../../../../utils/queryOptions';
import CustomersTable from '../../../../components/customers/CustomersTable';
import BackButton from '../../../../microcomponents/BackButton';
import { useState } from 'react';
import { apiV1 } from '../../../../utils/apiClient';
import RenameCustomersGroupsModal from '../../../../components/customers/RenameCustomersGroupModal';
import CustomersGroupOptions from '../../../../components/customers/CustomersGroupOptions';
import { operatorOptions } from '../../../../components/Inputs/CustomersRulesInput';
import { CustomersFilter, customersFilterPropertiesOptions } from '@next-is-free-monorepo/next-is-free-types';
import CustomerFiltersForm from '../../../../components/customers/CustomersRulesForm';
import PageLoader from '../../../../microcomponents/PageLoader';
import dayjs from 'dayjs';
import NAvatar from '../../../../../../libs/nif-web-components/src/microcomponents/NAvatar';
import { UserGroup02FreeIcons } from '@hugeicons/core-free-icons';
import { NButton } from '@next-is-free-monorepo/nif-web-components';

export const Route = createFileRoute(
  '/_authenticated/customers/groups/$groupId'
)({
  component: RouteComponent,
  pendingComponent: () => <PageLoader />
});


function RouteComponent() {

  const { groupId } = Route.useParams();
  const salesPoints = useSuspenseQuery(salesPointsQueryOptions());
  const group = useSuspenseQuery(customersGroupQueryOptions(groupId));

  const queryClient = useQueryClient();

  const [editRules, setEditRules] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);


  const updateRules = useMutation({
    mutationFn: async (values: CustomersFilter[]) => {

      const mappedValues = values.map((item) => {
        if (item.operator_type === 'date') {
          return {
            ...item,
            value: dayjs(item.value).add(dayjs(item.value).utcOffset(), 'minutes').toISOString() //cancel out the timezone
          };
        }
        return item
      });

      return (
        await apiV1.put(`/shop/customers/groups/${groupId}`, { ...group.data, rules: mappedValues })
      );
    },
    onSuccess: async () => {
      setEditRules(false);
      await queryClient.invalidateQueries({ queryKey: ['customers-groups', groupId] });
      await queryClient.invalidateQueries({ queryKey: ['customers-details', { search: '', groupId: groupId }] });
      message.success('Regole aggiornate');
    }
  });


  return (
    <div className={'p-4'}>
      <div className={'flex items-center justify-between mb-5'}>
        <div className={'flex items-center gap-2'}>
          <BackButton hideText fallBackRouteOptions={{ to: '/customers/groups' }} />
          <NAvatar hugeIcon={UserGroup02FreeIcons} />
          <h1 className={'font-medium text-xl'}>{group.data.name}</h1>
          <Tag
            color={group.data.type === 'manual' ? 'green' : 'blue'}>{group.data.type === 'manual' ? 'Manuale' : 'Automatico'}</Tag>
        </div>
        <CustomersGroupOptions group={group.data} />
      </div>
      {group.data.type === 'automatic' &&
        <Wrapper
          title={'Regole'}
          subtitle={'Le regole che definiscono chi fa parte di questo gruppo'}
          disabled={true}
          className={'mb-4'}
          actionComponent={<NButton onClick={() => setEditRules(true)} color={'secondary'} size={'small'}>Modifica</NButton>}
        >
          <div>
            <div className={'flex gap-2 flex-wrap'}>
              {group.data.rules?.map((rule) => (
                <div className={'py-1 px-3 rounded-full bg-white flex gap-1'}>
                  <p className={'font-medium'}>
                    {customersFilterPropertiesOptions.find(item => item.value === rule.property)?.label}
                    {rule.property === 'metadata' && rule.metadata_key}
                  </p>
                  <p className={'text-slate-500'}>
                    {operatorOptions[rule.operator_type].find((item) => item.value === rule.operator)?.label}
                  </p>
                  <p className={'font-medium'}>
                    {rule.operator_type === 'date' ? dayjs(rule.value).format('DD/MM/YYYY') : rule.operator_type === 'enum' ?
                      salesPoints.data.find((item) => item.id === rule.value)!.name : rule.value
                    }
                  </p>
                </div>
              ))}
            </div>
          </div>
          <CustomerFiltersForm onFinish={updateRules.mutate} open={editRules} onClose={() => setEditRules(false)}
                               rules={group.data.rules} />
        </Wrapper>
      }
      <CustomersTable group={group.data} />
      <RenameCustomersGroupsModal group={group.data} open={renameModalOpen || !group.data.name}
                                  onClose={() => setRenameModalOpen(false)} />
    </div>
  );
}
